import type { LeadFormStepSchema } from "@shared/leadFormRules";

/**
 * The public shape of a resolved lead form config, as the page payload ships it.
 *
 * Mirrors `serializeConfig` in `server/services/leadFormConfig.js` — that function is the
 * source of truth, and this type must follow it, not the other way round. Deliberately
 * separate from the admin `ILeadFormConfig`: the admin type carries lifecycle fields
 * (`is_enabled`) that the public payload never includes, because a disabled config is
 * resolved to `null` server-side and never reaches a visitor at all.
 */
export interface PublicLeadFormConfig {
  id: number;
  name: string;
  /** Presentation surface, locked at university level: inline form vs popup. */
  form_type: "fixed" | "popup";
  template_key: string;
  field_schema: { version?: number; steps?: LeadFormStepSchema[] } | null;
  copy_config?: Record<string, unknown> | null;
  theme_config?: Record<string, unknown> | null;
  /**
   * `custom_lead_form.lead_destination` — where this form's leads are delivered, published
   * so the CLIENT can pick the right attribution rules.
   *
   * `lsq` reads only `utm_*` — kept for the session, so they survive navigation — and stores
   * "" when there are none; `erp` reads the current page's bare `?source=/media=/campaign=`
   * triplet and falls back to the university source and
   * "Direct". The non-verified capture MINTS the row in the browser, so the rule has to run
   * there. `null` (or absent, on an older payload) means not chosen — treated as `erp`,
   * which is where such a form actually drops.
   */
  lead_destination?: string | null;
  /**
   * POPUP ONLY — when this form opens by itself. Absent on older payloads, and forced
   * all-off server-side for an inline form, so `readPopupTriggers` is always safe to call.
   */
  trigger_config?: PopupTriggerConfig | null;
}

/**
 * When a popup custom form opens without a CTA click.
 *
 * Mirrors `normalizePopupTriggers` in `server/constants/leadFormTriggers.js` — that file is
 * the source of truth for the shape, the units and the bounds, and this type follows it.
 *
 * These are FORM-level settings, not skin settings: every design wants the same answer to
 * "open after 30 seconds", and a skin is data plus class strings with no timers of its own.
 * They are also not university settings — a custom form is linked per page, so two pages
 * running two popups need two timings. The legacy popup's equivalents live on the
 * university's `lead_drop_popup` row (`enable_delay_form` / `enable_exit_form` /
 * `popup_display_interval_1..3`) and are read only when NO custom popup applies.
 */
export interface PopupTriggerConfig {
  /** Open on a timer. Meaningless without at least one interval; refused at save. */
  auto_open_enabled: boolean;
  /**
   * The delays, in SECONDS, in order — the first is the wait after the page settles, each
   * later one the wait after the previous close. At most three.
   *
   * Seconds, unlike the legacy fields' milliseconds: a box labelled "(ms)" is a standing
   * invitation to type 30 and get an instant popup. The two are never read together.
   */
  auto_open_intervals: number[];
  /** Open when the visitor moves to leave — mouse out of the viewport, or a mobile scroll. */
  exit_intent_enabled: boolean;
}

/** No triggers: the popup opens from a CTA and nothing else. Today's behaviour everywhere. */
export const EMPTY_POPUP_TRIGGERS: PopupTriggerConfig = {
  auto_open_enabled: false,
  auto_open_intervals: [],
  exit_intent_enabled: false,
};

/**
 * Read a stored `trigger_config` into a complete object.
 *
 * The server already normalizes on the way out, so this exists for the two cases it cannot
 * cover: a payload from before the column shipped, and a row being edited in the admin
 * before it has ever been saved. Defensive rather than validating — bounds and the surface
 * gate are the server's job, and re-deciding them here is how the two ends drift.
 */
export const readPopupTriggers = (value: unknown): PopupTriggerConfig => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return { ...EMPTY_POPUP_TRIGGERS };
  const raw = value as Partial<PopupTriggerConfig>;
  return {
    auto_open_enabled: raw.auto_open_enabled === true,
    auto_open_intervals: Array.isArray(raw.auto_open_intervals)
      ? raw.auto_open_intervals.map(Number).filter((n) => Number.isFinite(n) && n > 0)
      : [],
    exit_intent_enabled: raw.exit_intent_enabled === true,
  };
};

/** Which tier the config was resolved from. `legacy` means no config applies. */
export type LeadFormSource = "page" | "course" | "university" | "legacy";

/**
 * Is this resolved config actually renderable?
 *
 * A config with no steps is an authoring state, not a form — rendering it would give the
 * visitor an empty box with a Submit button and, worse, would REPLACE the working legacy
 * form. Falling back is always the safe direction.
 */
export const isRenderableLeadFormConfig = (
  config: PublicLeadFormConfig | null | undefined,
): config is PublicLeadFormConfig =>
  Boolean(config?.id) && Array.isArray(config?.field_schema?.steps) && config!.field_schema!.steps!.length > 0;
